<script setup lang="ts">
import router from "@/router";
import { useUserStore } from "@/stores/user";
import { defineEmits, computed, ref, onMounted } from "vue";
import { useRoute, RouterLink } from "vue-router";
import client from "@/utils/axios";

const userStore = useUserStore();
const route = useRoute();
const currentLang = ref(userStore.getCurrentLanguage);

const pictureUrl = ref(userStore.getAvatar);

onMounted(async () => {
  if (!pictureUrl.value) {
    try {
      const response = await client.get("/google-picture");
      pictureUrl.value = response.data.pictureUrl;
    } catch (error) {
      console.error("Error fetching Google picture:", error);
    }
  }
});

type Emit = {
  (event: "onPhaseSelect", phaseId: number): void;
  (event: "selectStep", stepId: number): void;
};

defineEmits<Emit>();

const logout = () => {
  userStore.logout();
  router.push("/login");
};

const activeMenu = computed(() => route.path);

const toggleLanguage = () => {
  let language = userStore.getCurrentLanguage;
  currentLang.value = language === "en" ? "de" : "en";
  userStore.changeLanguage(language === "en" ? "de" : "en");
};
</script>

<template>
  <div
    class="md:flex flex-col relative w-[48px] h-full m-[2px] p-3 pl-[10px] bg-[#E5E4E2] rounded-[4px]"
  >
    <!-- Top -->
    <div class="flex items-center gap-2">
      <!-- Smalt icon -->
      <RouterLink
        to="/"
        class="w-[24px] h-[24px] flex items-center justify-center cursor-pointer"
      >
        <img src="@/assets/smalt-home-logo.svg" alt="Smalt icon" />
      </RouterLink>
    </div>
    <!-- Sidebar links -->
    <div class="mt-[24px] flex flex-col items-start gap-2">
      <RouterLink
        to="/"
        class="group h-[28px] w-[28px] flex items-center justify-center bg-transparent rounded-primary-radius cursor-pointer hover:bg-[#d3d1cd] hover:shadow-[-4px_2px_4px_rgba(0,0,0,0.06)] duration-300"
        :class="
          activeMenu === '/' || activeMenu.toLowerCase().includes('projects')
            ? 'bg-white shadow-[-4px_2px_4px_rgba(0,0,0,0.06)]'
            : 'bg-transparent shadow-none'
        "
      >
        <img
          v-if="
            activeMenu === '/' || activeMenu.toLowerCase().includes('projects')
          "
          class="w-[24px] h-[24px]"
          src="@/assets/sidemenu-menu-icon.svg"
          alt="Menu"
        />
        <img
          v-else
          class="w-[24px] h-[24px]"
          src="@/assets/sidemenu-menu-icon-gray.svg"
          alt="Menu"
        />
      </RouterLink>
      <RouterLink
        to="/build"
        class="group h-[28px] w-[28px] flex items-center justify-center bg-transparent rounded-primary-radius cursor-pointer hover:bg-[#d3d1cd] hover:shadow-[-4px_2px_4px_rgba(0,0,0,0.06)] duration-300"
        :class="
          activeMenu.toLowerCase().includes('build')
            ? 'bg-white shadow-[-4px_2px_4px_rgba(0,0,0,0.06)]'
            : 'bg-transparent shadow-none'
        "
      >
        <img
          v-if="activeMenu.toLowerCase().includes('build')"
          class="w-[18px] h-[18px]"
          src="@/assets/edit-plus-active.svg"
          alt="Menu"
        />
        <img v-else src="@/assets/edit-plus-inactive.svg" alt="" />
      </RouterLink>

      <RouterLink
        to="/installers"
        class="group h-[28px] w-[28px] flex items-center justify-center bg-transparent rounded-primary-radius cursor-pointer hover:bg-[#d3d1cd] hover:shadow-[-4px_2px_4px_rgba(0,0,0,0.06)] duration-300"
        :class="
          activeMenu.toLowerCase().includes('installers')
            ? 'bg-white shadow-[-4px_2px_4px_rgba(0,0,0,0.06)]'
            : 'bg-transparent shadow-none'
        "
      >
        <img
          v-if="activeMenu.toLowerCase().includes('installers')"
          class="w-[20px] h-[20px]"
          src="@/assets/users-v2.svg"
          alt="Menu"
        />
        <img v-else src="@/assets/users-v2-gray.svg" alt="Menu" />
      </RouterLink>

      <RouterLink
        to="/marketing/reviews"
        class="group h-[28px] w-[28px] flex items-center justify-center bg-transparent rounded-primary-radius cursor-pointer hover:bg-[#d3d1cd] hover:shadow-[-4px_2px_4px_rgba(0,0,0,0.06)] duration-300"
        :class="
          activeMenu.toLowerCase().includes('marketing')
            ? 'bg-white shadow-[-4px_2px_4px_rgba(0,0,0,0.06)]'
            : 'bg-transparent shadow-none'
        "
      >
        <img
          class="w-[20px] h-[20px]"
          src="@/assets/star-icon.svg"
          alt="Marketing"
        />
      </RouterLink>
    </div>

    <!-- Bottom -->
    <div class="mt-auto flex flex-col items-start gap-[6px] justify-center">
      <!-- User -->
      <!-- <RouterLink
        to="/"
        class="hidden sm:flex items-center gap-1 p-2 cursor-pointer"
      >
        <img src="@/assets/user-icon.svg" alt="User icon" />
      </RouterLink> -->
      <!-- Logout -->
      <div>
        <button
          @click="toggleLanguage"
          class="group h-[28px] w-[28px] flex items-center justify-center bg-transparent rounded-primary-radius cursor-pointer hover:bg-[#d3d1cd] hover:shadow-[-4px_2px_4px_rgba(0,0,0,0.06)] duration-300"
        >
          <span class="text-sm text-[#898790] font-semibold">{{
            currentLang === "de" ? "EN" : "DE"
          }}</span>
        </button>
      </div>
      <div
        v-if="pictureUrl"
        class="flex items-center w-[28px] h-[28px] justify-center m-auto rounded-full mb-[6px]"
      >
        <img
          v-if="pictureUrl"
          class="w-[20px] h-[20px] rounded-full border-[2px] border-[#898790]"
          :src="pictureUrl"
          alt=""
        />
        <img
          v-else
          class="w-[20px] h-[20px] rounded-full border-[2px] border-[#898790]"
          src="@/assets/user-icon.svg"
          alt=""
        />
      </div>
      <button
        @click="logout"
        class="group h-[28px] w-[28px] flex items-center justify-center bg-transparent rounded-primary-radius cursor-pointer hover:bg-[#d3d1cd] hover:shadow-[-4px_2px_4px_rgba(0,0,0,0.06)] duration-300"
      >
        <img src="@/assets/logout-redesign.svg" alt="Logout" />
      </button>
    </div>
  </div>
</template>
